import {
  type FloatingPosition,
  HoverCard,
  Text,
  type TextProps,
} from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

type Props = {
  description: string;
  lineClamp: number;
  color?: TextProps['c'];
  size?: TextProps['size'];
  dropdownTestId?: string;
  dropdownWidth?: number;
  dropdownPosition?: FloatingPosition;
  targetTestId?: string;
};

export const ExpenseDescriptionHoverCard = ({
  description,
  lineClamp,
  color,
  size = 'sm',
  dropdownTestId,
  dropdownWidth = 660,
  dropdownPosition = 'top-start',
  targetTestId,
}: Props) => {
  const descriptionRef = useRef<HTMLSpanElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const node = descriptionRef.current;
    if (!node) return;

    let frame = 0;

    const updateOverflowState = () => {
      const currentNode = descriptionRef.current;
      if (!currentNode) return;

      // Skip transient layout states (e.g. width 0 during mount/resize).
      if (currentNode.clientWidth === 0 || currentNode.clientHeight === 0) {
        setIsOverflowing(false);
        return;
      }

      setIsOverflowing(currentNode.scrollHeight > currentNode.clientHeight);
    };

    const scheduleUpdate = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateOverflowState);
    };

    scheduleUpdate();

    let observer: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleUpdate);
      observer.observe(node);
    }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [description, lineClamp]);

  const text = (
    <Text
      ref={descriptionRef}
      component="span"
      lineClamp={lineClamp}
      size={size}
      c={color}
      sx={{
        wordBreak: 'break-word',
        whiteSpace: 'normal',
      }}
      data-testid={targetTestId}
    >
      {description}
    </Text>
  );

  if (!isOverflowing) return text;

  return (
    <HoverCard
      position={dropdownPosition}
      withArrow={false}
      withinPortal
      width={dropdownWidth}
      shadow="md"
      radius="md"
      arrowOffset={12}
    >
      <HoverCard.Target>{text}</HoverCard.Target>
      <HoverCard.Dropdown p="xs" maw={660} data-testid={dropdownTestId}>
        <Text
          component="span"
          size={size}
          display="block"
          sx={{
            wordBreak: 'break-word',
          }}
        >
          {description}
        </Text>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
